// Real-mode Webhooks fetchers — swap-in for the *Mock() functions (task [3.2]).
// Relative paths; the installed fetch-patch handles VITE_API_URL prefixing, the
// MSAL bearer, and non-2xx rejection. Same untested-against-real-backend caveat
// as every other domain's real.ts (see proposal risk table) — this file is
// wired but never exercised without VITE_API_URL set.

import { z } from 'zod';

import type {
	WebhookConfig,
	WebhookConfigPage,
	WebhookEvent,
	WebhookEventPage,
} from './mock';
import { WEBHOOK_EVENT_TYPES } from './mock';

const webhookEventTypeSchema = z.enum(WEBHOOK_EVENT_TYPES);
const webhookEventStatusSchema = z.enum(['success', 'danger']);

const webhookConfigSchema = z.object({
	id: z.string(),
	url: z.string(),
	listeningFor: z.array(webhookEventTypeSchema),
	status: z.enum(['enabled', 'disabled']),
	createdAt: z.string(),
	signingSecret: z.string(),
});

const webhookConfigPageSchema = z.object({
	configs: z.array(webhookConfigSchema),
	nextCursor: z.string().nullable(),
});

const webhookEventSchema = z.object({
	id: z.string(),
	type: webhookEventTypeSchema,
	timestamp: z.string(),
	status: webhookEventStatusSchema,
	httpStatusCode: z.number(),
	attempts: z.number(),
	responseBody: z.string(),
	messagePayload: z.string(),
});

const webhookEventPageSchema = z.object({
	events: z.array(webhookEventSchema),
	nextCursor: z.string().nullable(),
});

export async function getWebhookConfig(): Promise<WebhookConfig> {
	const response = await fetch('/webhooks/config');
	const json: unknown = await response.json();
	return webhookConfigSchema.parse(json);
}

export async function getWebhookConfigs(params?: {
	cursor?: string | null;
	limit?: number;
}): Promise<WebhookConfigPage> {
	const search = new URLSearchParams();
	if (params?.cursor) search.set('cursor', params.cursor);
	if (params?.limit != null) search.set('limit', String(params.limit));
	const qs = search.toString();

	const response = await fetch(`/webhooks/configs${qs ? `?${qs}` : ''}`);
	const json: unknown = await response.json();
	return webhookConfigPageSchema.parse(json);
}

export async function getWebhookEvents(params?: {
	cursor?: string | null;
	limit?: number;
}): Promise<WebhookEventPage> {
	const search = new URLSearchParams();
	if (params?.cursor) search.set('cursor', params.cursor);
	if (params?.limit != null) search.set('limit', String(params.limit));
	const qs = search.toString();

	const response = await fetch(`/webhooks/events${qs ? `?${qs}` : ''}`);
	const json: unknown = await response.json();
	return webhookEventPageSchema.parse(json);
}

export async function replayWebhookEvent(id: string): Promise<WebhookEvent> {
	const response = await fetch(`/webhooks/events/${id}/replay`, {
		method: 'POST',
	});
	const json: unknown = await response.json();
	return webhookEventSchema.parse(json);
}
